import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ActivityFeedPersonaControl from './ActivityFeedPersonaControl';
import { OpenLinkActionType, OpenMailActionType } from './ActivityFeedActions';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import {
    asOwaDateInUserTz,
    formatShortMonthDay,
    isSameDay,
    formatUserTime,
    userDate,
} from 'owa-datetime';
import { observableNow } from 'owa-observable-datetime';
import * as MarkdownIt from 'markdown-it';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import {
    ActivityCardLayoutType,
    ActivityFeedCardDetailsProps,
} from '../utils/feedCardPresentationDetails';
import { activityFeedDismiss } from './WrappedAdaptiveCard.locstring.json';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { dismissItem } from '../actions/dismissItem';
import { ControlIcons } from 'owa-control-icons';
import { Depths } from '@uifabric/fluent-theme';
const styles = require('./ActivityFeedCard.scss');
const classNames = require('classnames/bind').bind(styles);

const ActivityFeedCard: React.FunctionComponent<ActivityFeedCardDetailsProps> = observer(
    function ActivityFeedCard(props: ActivityFeedCardDetailsProps) {
        const [cardHovered, setCardHovered] = React.useState<boolean>(false);
        const { itemId, item, onAction } = props;
        const defaultOnClickAction = {
            actionName: 'Action.OpenMail',
            messageId: item.messageId,
            itemId: itemId,
        } as OpenMailActionType;
        const actionType =
            item.cardOnClickAction == null ? defaultOnClickAction : item.cardOnClickAction;
        const clickHandler = React.useCallback(() => {
            onAction(itemId, actionType);
        }, [itemId, actionType]);
        var unseenIdentificatorClass = item.isSeen
            ? styles.unreadIndicator
            : classNames(styles.unreadIndicator, styles.unreadIndicatorVisible);
        const onMouseEnter = () => {
            setCardHovered(true);
        };
        const onMouseLeave = () => {
            setCardHovered(false);
        };
        return Object.keys(item).length === 0 ? null : (
            <div
                className={styles.activityFeedCard}
                onClick={clickHandler}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                style={cardHovered ? { boxShadow: Depths.depth16 } : { boxShadow: Depths.depth4 }}>
                <div className={styles.activityCardContent}>
                    <div className={styles.personaImage}>
                        <span className={unseenIdentificatorClass} />
                        <ActivityFeedPersonaControl personas={item.senderInfo} item={item} />
                    </div>
                    <div className={styles.activityCardBodyFlex}>
                        <ActivityFeedCardBody item={item} itemId={itemId} onAction={onAction} />
                        <ActivityFeedCardFooter item={item} itemId={itemId} onAction={onAction} />
                    </div>
                </div>
            </div>
        );
    }
);

const ActivityFeedCardFooter: React.FunctionComponent<ActivityFeedCardDetailsProps> = observer(
    function ActivityFeedCardFooter(props: ActivityFeedCardDetailsProps) {
        const heroButton = props.item.heroButtonProperties;
        const clickHandler = React.useCallback(
            (url: string) => {
                props.onAction(props.itemId, {
                    actionName: 'Action.OpenUrl',
                    messageId: props.item.messageId,
                    url: url,
                    itemId: props.itemId,
                } as OpenLinkActionType);
            },
            [props.itemId]
        );
        if (heroButton) {
            const title = heroButton.buttonTitle;
            const url = heroButton.buttonActionUrl;
            const urlClicked = () => {
                clickHandler(url);
            };
            return (
                <ActionButton className={styles.activityCardBodyHeroAction} onClick={urlClicked}>
                    {title}
                </ActionButton>
            );
        }
        return <div />;
    }
);

const ActivityFeedCardBody: React.FunctionComponent<ActivityFeedCardDetailsProps> = observer(
    function ActivityFeedCardBody(props: ActivityFeedCardDetailsProps) {
        let md = MarkdownIt();
        let bodyFlexStyle =
            props.item.cardType == ActivityCardLayoutType.MultiTitleActivityCard
                ? classNames(styles.activityCardInlineBodyFlex, styles.titleText3Lines)
                : classNames(styles.activityCardInlineBodyFlex, styles.titleText2Lines);
        const onDismissItem = React.useCallback(
            (event: React.MouseEvent<HTMLSpanElement>) => {
                dismissItem(props.itemId);
                event.stopPropagation();
            },
            [props.itemId]
        );

        return (
            /* tslint:disable:react-no-dangerous-html */
            <div className={bodyFlexStyle}>
                <p className={styles.titleText}>
                    <span>
                        <Icon
                            className={styles.activityFeedCardTypeIcon}
                            iconName={props.item.cardIcon}
                        />
                    </span>
                    <span
                        className={styles.inlineText}
                        dangerouslySetInnerHTML={{
                            __html: md.render(props.item.cardTitle),
                        }}></span>
                </p>
                <div className={styles.activityFeedTime}>
                    <IconButton
                        className={styles.itemDismiss}
                        iconProps={{ iconName: ControlIcons.Clear }}
                        onClick={onDismissItem}
                        title={activityFeedDismiss}
                        ariaLabel={activityFeedDismiss}
                    />
                    <div className={styles.date}>{getTimeStamp(props.item.timeStamp)}</div>
                </div>
            </div>
        );
    }
);

function getTimeStamp(timeStamp: Date): String {
    if (isSameDay(userDate(timeStamp.toString()), observableNow())) {
        return formatUserTime(asOwaDateInUserTz(new Date(timeStamp)));
    }
    return formatShortMonthDay(asOwaDateInUserTz(new Date(timeStamp)));
}

export default ActivityFeedCard;
